/**
 * WebM/Opus muxer for WebCodecs output
 * Creates a simple WebM container with Opus audio
 */

/**
 * Opus encoded chunk with metadata
 */
export interface OpusEncodedChunk {
  data: Uint8Array;
  timestamp: number; // microseconds
  duration: number; // microseconds
  isKeyFrame: boolean;
}

/**
 * WebM muxer configuration
 */
export interface WebMMuxerConfig {
  sampleRate: number;
  channelCount: number;
}

/**
 * Simple WebM muxer for Opus audio
 * Uses minimal EBML structure for audio-only WebM
 */
export class WebMOpusMuxer {
  private config: WebMMuxerConfig;
  private chunks: OpusEncodedChunk[] = [];

  constructor(config: WebMMuxerConfig) {
    this.config = config;
  }

  /**
   * Add an encoded audio chunk
   */
  addChunk(chunk: OpusEncodedChunk): void {
    this.chunks.push(chunk);
  }

  /**
   * Finalize and return the WebM blob
   */
  finalize(): Blob {
    const ebml = this.buildEBML();
    return new Blob([ebml], { type: 'audio/webm' });
  }

  private buildEBML(): Uint8Array {
    const chunks: Uint8Array[] = [];

    // EBML Header
    chunks.push(this.buildEBMLHeader());

    // Segment
    chunks.push(this.buildSegment());

    // Calculate total size
    const totalSize = chunks.reduce((sum, c) => sum + c.length, 0);
    const result = new Uint8Array(totalSize);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }

    return result;
  }

  private buildEBMLHeader(): Uint8Array {
    // Minimal EBML header for WebM
    return new Uint8Array([
      0x1a, 0x45, 0xdf, 0xa3, // EBML Element ID
      0x01, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x1f, // Size (31 bytes)
      0x42, 0x86, 0x81, 0x01, // EBMLVersion = 1
      0x42, 0xf7, 0x81, 0x01, // EBMLReadVersion = 1
      0x42, 0xf2, 0x81, 0x04, // EBMLMaxIDLength = 4
      0x42, 0xf3, 0x81, 0x08, // EBMLMaxSizeLength = 8
      0x42, 0x82, 0x84, 0x77, 0x65, 0x62, 0x6d, // DocType = "webm"
      0x42, 0x87, 0x81, 0x02, // DocTypeVersion = 2
      0x42, 0x85, 0x81, 0x02, // DocTypeReadVersion = 2
    ]);
  }

  private buildSegment(): Uint8Array {
    const parts: Uint8Array[] = [];

    // Segment Info
    parts.push(this.buildSegmentInfo());

    // Tracks
    parts.push(this.buildTracks());

    // Clusters with audio data
    parts.push(this.buildClusters());

    // Calculate segment content size
    const contentSize = parts.reduce((sum, p) => sum + p.length, 0);

    // Segment header (unknown size for streaming)
    const segmentHeader = new Uint8Array([
      0x18, 0x53, 0x80, 0x67, // Segment Element ID
      0x01, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, // Unknown size
    ]);

    // Combine
    const result = new Uint8Array(segmentHeader.length + contentSize);
    result.set(segmentHeader, 0);
    let offset = segmentHeader.length;
    for (const part of parts) {
      result.set(part, offset);
      offset += part.length;
    }

    return result;
  }

  private buildSegmentInfo(): Uint8Array {
    // Minimal segment info
    const timecodeScale = 1000000; // 1ms precision (nanoseconds per timecode unit)
    const muxingApp = 'OmniScribe';
    const writingApp = 'OmniScribe';

    // Calculate total duration in milliseconds
    const durationMs = this.calculateDurationMs();

    const content: number[] = [];

    // TimecodeScale (1ms = 1000000 nanoseconds)
    content.push(0x2a, 0xd7, 0xb1, 0x83); // Element ID + size (3 bytes)
    content.push(
      (timecodeScale >> 16) & 0xff,
      (timecodeScale >> 8) & 0xff,
      timecodeScale & 0xff
    );

    // Duration (float, in timecode units = milliseconds)
    // Element ID: 0x4489, using 8-byte float for precision
    const durationBytes = this.floatToBytes64(durationMs);
    content.push(0x44, 0x89, 0x88); // Element ID + size (8 bytes)
    content.push(...durationBytes);

    // MuxingApp
    content.push(0x4d, 0x80, 0x80 | muxingApp.length);
    for (let i = 0; i < muxingApp.length; i++) {
      content.push(muxingApp.charCodeAt(i));
    }

    // WritingApp
    content.push(0x57, 0x41, 0x80 | writingApp.length);
    for (let i = 0; i < writingApp.length; i++) {
      content.push(writingApp.charCodeAt(i));
    }

    // Build Info element
    const infoContent = new Uint8Array(content);
    const infoHeader = this.buildElementHeader(0x1549a966, infoContent.length);

    const result = new Uint8Array(infoHeader.length + infoContent.length);
    result.set(infoHeader, 0);
    result.set(infoContent, infoHeader.length);

    return result;
  }

  /**
   * Calculate total duration in milliseconds from chunks
   */
  private calculateDurationMs(): number {
    if (this.chunks.length === 0) {
      return 0;
    }

    let maxEndTime = 0;
    for (const chunk of this.chunks) {
      const endTime = (chunk.timestamp + chunk.duration) / 1000; // microseconds to milliseconds
      if (endTime > maxEndTime) {
        maxEndTime = endTime;
      }
    }

    return maxEndTime;
  }

  private buildTracks(): Uint8Array {
    const trackContent = this.buildAudioTrack();

    // Tracks element header
    const tracksHeader = this.buildElementHeader(0x1654ae6b, trackContent.length);

    const result = new Uint8Array(tracksHeader.length + trackContent.length);
    result.set(tracksHeader, 0);
    result.set(trackContent, tracksHeader.length);

    return result;
  }

  private buildAudioTrack(): Uint8Array {
    const content: number[] = [];

    // TrackNumber = 1
    content.push(0xd7, 0x81, 0x01);

    // TrackUID = 1
    content.push(0x73, 0xc5, 0x81, 0x01);

    // TrackType = 2 (audio)
    content.push(0x83, 0x81, 0x02);

    // CodecID = "A_OPUS"
    const codecId = 'A_OPUS';
    content.push(0x86, 0x80 | codecId.length);
    for (let i = 0; i < codecId.length; i++) {
      content.push(codecId.charCodeAt(i));
    }

    // CodecPrivate (OpusHead) - REQUIRED for Opus playback
    const opusHead = this.buildOpusHead();
    const codecPrivateHeader = this.buildElementHeader(0x63a2, opusHead.length);
    for (let i = 0; i < codecPrivateHeader.length; i++) {
      content.push(codecPrivateHeader[i]);
    }
    for (let i = 0; i < opusHead.length; i++) {
      content.push(opusHead[i]);
    }

    // Audio settings
    const audioSettings = this.buildAudioSettings();
    content.push(...audioSettings);

    // Build TrackEntry element
    const trackEntryContent = new Uint8Array(content);
    const trackEntryHeader = this.buildElementHeader(0xae, trackEntryContent.length);

    const result = new Uint8Array(trackEntryHeader.length + trackEntryContent.length);
    result.set(trackEntryHeader, 0);
    result.set(trackEntryContent, trackEntryHeader.length);

    return result;
  }

  private buildAudioSettings(): number[] {
    const content: number[] = [];

    // SamplingFrequency
    const sampleRateBytes = this.floatToBytes(this.config.sampleRate);
    content.push(0xb5, 0x80 | sampleRateBytes.length);
    content.push(...sampleRateBytes);

    // Channels
    content.push(0x9f, 0x81, this.config.channelCount);

    // Audio element header (0xE1)
    const audioContent = new Uint8Array(content);
    const result: number[] = [0xe1, 0x80 | audioContent.length];
    result.push(...audioContent);

    return result;
  }

  private buildClusters(): Uint8Array {
    if (this.chunks.length === 0) {
      return new Uint8Array(0);
    }

    const parts: Uint8Array[] = [];
    let clusterStartTime = 0;
    let currentClusterBlocks: Uint8Array[] = [];

    for (let i = 0; i < this.chunks.length; i++) {
      const chunk = this.chunks[i];
      const timeMs = Math.floor(chunk.timestamp / 1000); // Convert to ms

      // Start new cluster every 32KB or 5 seconds
      const clusterSize = currentClusterBlocks.reduce((s, b) => s + b.length, 0);
      const timeSinceClusterStart = timeMs - clusterStartTime;

      if (currentClusterBlocks.length > 0 && (clusterSize > 32000 || timeSinceClusterStart > 5000)) {
        parts.push(this.buildCluster(clusterStartTime, currentClusterBlocks));
        currentClusterBlocks = [];
        clusterStartTime = timeMs;
      }

      // Build SimpleBlock
      const relativeTime = timeMs - clusterStartTime;
      const block = this.buildSimpleBlock(chunk.data, relativeTime, chunk.isKeyFrame);
      currentClusterBlocks.push(block);
    }

    // Final cluster
    if (currentClusterBlocks.length > 0) {
      parts.push(this.buildCluster(clusterStartTime, currentClusterBlocks));
    }

    // Combine all clusters
    const totalSize = parts.reduce((sum, p) => sum + p.length, 0);
    const result = new Uint8Array(totalSize);
    let offset = 0;
    for (const part of parts) {
      result.set(part, offset);
      offset += part.length;
    }

    return result;
  }

  private buildCluster(timecodeMs: number, blocks: Uint8Array[]): Uint8Array {
    // Cluster Timecode
    const timecodeElement = this.buildVintElement(0xe7, this.vintEncode(timecodeMs));

    // Calculate content size
    const blocksSize = blocks.reduce((sum, b) => sum + b.length, 0);
    const contentSize = timecodeElement.length + blocksSize;

    // Cluster header
    const clusterHeader = this.buildElementHeader(0x1f43b675, contentSize);

    // Combine
    const result = new Uint8Array(clusterHeader.length + contentSize);
    result.set(clusterHeader, 0);
    result.set(timecodeElement, clusterHeader.length);

    let offset = clusterHeader.length + timecodeElement.length;
    for (const block of blocks) {
      result.set(block, offset);
      offset += block.length;
    }

    return result;
  }

  private buildSimpleBlock(
    data: Uint8Array,
    relativeTimeMs: number,
    isKeyFrame: boolean
  ): Uint8Array {
    // SimpleBlock structure:
    // - Track number (VINT)
    // - Relative timecode (signed 16-bit)
    // - Flags (1 byte)
    // - Data

    const trackNum = new Uint8Array([0x81]); // Track 1 as VINT
    const timecode = new Uint8Array([
      (relativeTimeMs >> 8) & 0xff,
      relativeTimeMs & 0xff,
    ]);
    const flags = new Uint8Array([isKeyFrame ? 0x80 : 0x00]);

    const blockContent = new Uint8Array(
      trackNum.length + timecode.length + flags.length + data.length
    );
    blockContent.set(trackNum, 0);
    blockContent.set(timecode, trackNum.length);
    blockContent.set(flags, trackNum.length + timecode.length);
    blockContent.set(data, trackNum.length + timecode.length + flags.length);

    // SimpleBlock element header (0xA3)
    const header = this.buildElementHeader(0xa3, blockContent.length);

    const result = new Uint8Array(header.length + blockContent.length);
    result.set(header, 0);
    result.set(blockContent, header.length);

    return result;
  }

  private buildElementHeader(elementId: number, size: number): Uint8Array {
    const idBytes = this.encodeElementId(elementId);
    const sizeBytes = this.encodeVintSize(size);

    const result = new Uint8Array(idBytes.length + sizeBytes.length);
    result.set(idBytes, 0);
    result.set(sizeBytes, idBytes.length);

    return result;
  }

  private buildVintElement(elementId: number, value: Uint8Array): Uint8Array {
    const idBytes = this.encodeElementId(elementId);
    const sizeBytes = this.encodeVintSize(value.length);

    const result = new Uint8Array(idBytes.length + sizeBytes.length + value.length);
    result.set(idBytes, 0);
    result.set(sizeBytes, idBytes.length);
    result.set(value, idBytes.length + sizeBytes.length);

    return result;
  }

  private encodeElementId(id: number): Uint8Array {
    if (id <= 0xff) {
      return new Uint8Array([id]);
    } else if (id <= 0xffff) {
      return new Uint8Array([(id >> 8) & 0xff, id & 0xff]);
    } else if (id <= 0xffffff) {
      return new Uint8Array([(id >> 16) & 0xff, (id >> 8) & 0xff, id & 0xff]);
    } else {
      return new Uint8Array([
        (id >> 24) & 0xff,
        (id >> 16) & 0xff,
        (id >> 8) & 0xff,
        id & 0xff,
      ]);
    }
  }

  private encodeVintSize(size: number): Uint8Array {
    if (size < 0x80 - 1) {
      return new Uint8Array([0x80 | size]);
    } else if (size < 0x4000 - 1) {
      return new Uint8Array([0x40 | ((size >> 8) & 0x3f), size & 0xff]);
    } else if (size < 0x200000 - 1) {
      return new Uint8Array([
        0x20 | ((size >> 16) & 0x1f),
        (size >> 8) & 0xff,
        size & 0xff,
      ]);
    } else {
      return new Uint8Array([
        0x10 | ((size >> 24) & 0x0f),
        (size >> 16) & 0xff,
        (size >> 8) & 0xff,
        size & 0xff,
      ]);
    }
  }

  private vintEncode(value: number): Uint8Array {
    if (value < 0x80) {
      return new Uint8Array([value]);
    } else if (value < 0x4000) {
      return new Uint8Array([(value >> 8) & 0xff, value & 0xff]);
    } else if (value < 0x200000) {
      return new Uint8Array([
        (value >> 16) & 0xff,
        (value >> 8) & 0xff,
        value & 0xff,
      ]);
    } else {
      return new Uint8Array([
        (value >> 24) & 0xff,
        (value >> 16) & 0xff,
        (value >> 8) & 0xff,
        value & 0xff,
      ]);
    }
  }

  private floatToBytes(value: number): number[] {
    const buffer = new ArrayBuffer(4);
    const view = new DataView(buffer);
    view.setFloat32(0, value, false); // Big-endian
    return [view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3)];
  }

  /**
   * Convert a number to 64-bit float bytes (big-endian)
   */
  private floatToBytes64(value: number): number[] {
    const buffer = new ArrayBuffer(8);
    const view = new DataView(buffer);
    view.setFloat64(0, value, false); // Big-endian
    return [
      view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3),
      view.getUint8(4), view.getUint8(5), view.getUint8(6), view.getUint8(7),
    ];
  }

  /**
   * Build OpusHead structure for CodecPrivate
   * See: https://wiki.xiph.org/OggOpus#ID_Header
   *
   * OpusHead format (19 bytes minimum):
   * - Magic signature: "OpusHead" (8 bytes)
   * - Version: 1 (1 byte)
   * - Channel count (1 byte)
   * - Pre-skip: samples to skip at start (2 bytes, little-endian)
   * - Input sample rate: original rate (4 bytes, little-endian)
   * - Output gain: 0 dB (2 bytes, little-endian)
   * - Mapping family: 0 for mono/stereo (1 byte)
   */
  private buildOpusHead(): Uint8Array {
    const head = new Uint8Array(19);

    // Magic signature: "OpusHead"
    head[0] = 0x4f; // O
    head[1] = 0x70; // p
    head[2] = 0x75; // u
    head[3] = 0x73; // s
    head[4] = 0x48; // H
    head[5] = 0x65; // e
    head[6] = 0x61; // a
    head[7] = 0x64; // d

    // Version = 1
    head[8] = 0x01;

    // Channel count
    head[9] = this.config.channelCount;

    // Pre-skip (3840 samples = 80ms at 48kHz, standard for Opus)
    // Little-endian
    const preSkip = 3840;
    head[10] = preSkip & 0xff;
    head[11] = (preSkip >> 8) & 0xff;

    // Input sample rate (little-endian)
    const sampleRate = this.config.sampleRate;
    head[12] = sampleRate & 0xff;
    head[13] = (sampleRate >> 8) & 0xff;
    head[14] = (sampleRate >> 16) & 0xff;
    head[15] = (sampleRate >> 24) & 0xff;

    // Output gain = 0 (little-endian)
    head[16] = 0x00;
    head[17] = 0x00;

    // Mapping family = 0 (mono or stereo, no channel mapping table)
    head[18] = 0x00;

    return head;
  }
}
